// Timeline filter — the pure narrowing of the timeline by search + chips.
//
// The Timeline lens composes three orthogonal narrowings over the resolved
// rows, all pure (no I/O, no clock): the shared search DSL (fuzzy text +
// `by:` actor axis, reused verbatim from `Search/query.ts`), the kind-group
// chips, the actor chips, and the "show internal events" toggle (off by
// default). Grouping into date sections is the shell's job (rows arrive
// pre-sectioned); this only DROPS rows and then any now-empty section.
//
// Kept out of the component so the narrowing is unit-testable in isolation —
// types cover the row shapes, this covers the filter SEMANTICS.

export type { TimelineGroup };
export { filterSections, rowGroup, TIMELINE_GROUPS };

import type { ByActor } from "../../Search/query.js";
import { evaluate, parseQuery } from "../../Search/query.js";
import type { TimelineActor, TimelineRow, TimelineSection } from "../types.js";

// The timeline speaks "you"; the `by:` DSL axis speaks "me". Map at the seam
// so `by:me` narrows to the author's own moments. Total over TimelineActor.
const byActorOf = (actor: TimelineActor): ByActor =>
    actor === "you" ? "me" : actor;

// The coarse filter buckets the kind chips expose — several row kinds fold
// into one chip (all the material-lifecycle kinds share the "material" chip).
type TimelineGroup =
    | "source"
    | "material"
    | "export"
    | "fact"
    | "session"
    | "project";

const TIMELINE_GROUPS: readonly TimelineGroup[] = [
    "source",
    "material",
    "export",
    "fact",
    "session",
    "project",
];

// Which chip a row answers to. Total over `TimelineKind`.
const rowGroup = (row: TimelineRow): TimelineGroup => {
    switch (row.type) {
        case "ingested_source":
            return "source";
        case "authored_material":
        case "edited_material":
        case "deleted_resource":
        case "restored_resource":
        case "version_restored":
            return "material";
        case "packaged_material":
            return "export";
        case "asserted_fact":
        case "retracted_fact":
            return "fact";
        case "session_started":
            return "session";
        case "project_started":
            return "project";
    }
};

// The searchable text of a row — its resolved subject plus a coarse kind word,
// so `type:`-less fuzzy text ("packaged") still finds a packaging moment.
const rowText = (row: TimelineRow): string => `${row.subject} ${row.type}`;

type FilterInput = {
    readonly queryText: string;
    // The kinds currently ON. A row survives when its group is in this set.
    readonly activeGroups: ReadonlySet<TimelineGroup>;
    // The actors currently ON. A row survives when its actor is in this set.
    readonly activeActors: ReadonlySet<TimelineActor>;
    readonly showInternal: boolean;
};

// Narrow one row-set by the chip + toggle gates (everything but search).
const passesChips = (row: TimelineRow, input: FilterInput): boolean => {
    if (row.internal === true && !input.showInternal) return false;
    if (!input.activeGroups.has(rowGroup(row))) return false;
    if (!input.activeActors.has(row.actor)) return false;
    return true;
};

// Apply all narrowings across every section, dropping now-empty sections.
// Search runs over the MOMENTS (resolved subject + kind), never the raw log,
// via the shared DSL — so `by:assistant fractions` and `type`-less fuzzy text
// both compose here. An empty query passes every (chip-surviving) row.
const filterSections = (
    sections: readonly TimelineSection[],
    input: FilterInput,
): readonly TimelineSection[] => {
    const query = parseQuery(input.queryText);
    return sections
        .map((section) => {
            const chipSurvivors = section.rows.filter((row) =>
                passesChips(row, input),
            );
            const items = chipSurvivors.map((row) => ({
                id: row.id,
                title: rowText(row),
                kind: rowGroup(row),
                actor: byActorOf(row.actor),
            }));
            const ranked = new Set(
                evaluate(query, items).map((scored) => scored.item.id),
            );
            const rows = chipSurvivors.filter((row) => ranked.has(row.id));
            return { label: section.label, rows };
        })
        .filter((section) => section.rows.length > 0);
};
